import { Modal, Space } from 'antd';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import useOrganizations from '../../../hooks/organizationPage/useOrganizations';
import useUserById, { deriveCustomizedDetailedUser } from '../../../hooks/useUserById';
import useYupValidationResolver from '../../../hooks/useValidationResolver';
import { CreateOrganizationPostData } from '../../../model/types';
import { createOrganization } from '../../../services/organization/organizationServices';
import { updateUser } from '../../../services/people/userServices';
import { grispiNotification } from '../../../store/useNotificationStore';
import { isCustomerPage, isTicketsPage } from '../../../utils/routeHelper';
import { resolveRelatedCustomerOrganizationPage, resolveRelatedOrganizationTicketsPage } from '../../BreadCrumbs';
import GrispiInput from '../../GrispiInput';

export interface OrganizationModalProps {
    userId?: string;
    isOpen: boolean;
    closeOrganizationModal: () => void;
}
export default function OrganizationCreateModal({ userId, isOpen, closeOrganizationModal }: OrganizationModalProps) {
    const { asPath, push } = useRouter();
    const { t } = useTranslation('organizations');
    const { t: tForm } = useTranslation('forms');
    const { data, mutate: mutateUser } = useUserById(userId);
    const userInfo = deriveCustomizedDetailedUser(data);
    const { mutate: mutateOrg } = useOrganizations();
    const defaultValues = { name: null };
    const validationSchema = useMemo(
        () =>
            object().shape({
                name: string().nullable().required(tForm('form.required'))
            }),

        [tForm]
    );
    const resolver = useYupValidationResolver(validationSchema);

    const {
        handleSubmit,
        control,
        reset,
        formState: { isDirty }
    } = useForm<CreateOrganizationPostData>({
        mode: 'onChange',
        defaultValues,
        resolver
    });
    const onSubmit: SubmitHandler<CreateOrganizationPostData> = async (formData) => {
        try {
            // console.log(`OrganizationCreateModal onSubmit postData:`, formData);

            const { id: createdOrganizationID } = await createOrganization(formData);
            if (createdOrganizationID && userInfo?.id) {
                await updateUser({
                    organization: createdOrganizationID,
                    role: userInfo?.role?.authority,
                    userId: userInfo?.id
                });

                await mutateOrg();
                await mutateUser();
                if (isCustomerPage(asPath)) {
                    push(resolveRelatedCustomerOrganizationPage(asPath));
                }
                if (isTicketsPage(asPath)) {
                    push(resolveRelatedOrganizationTicketsPage(asPath));
                }
            } else if (createdOrganizationID) {
                push(`/organizations/${createdOrganizationID}/tickets?page=1&size=5`);
            }

            closeOrganizationModal();
            reset();
        } catch (error) {
            grispiNotification('error', error?.response?.data?.message ?? error?.message);
        }
    };
    const handleOk = async () => {
        await handleSubmit(onSubmit)();
    };

    const handleCancel = () => {
        reset();
        closeOrganizationModal();
    };
    return (
        <>
            <Modal
                title={t('modalTitle')}
                open={isOpen}
                onOk={handleOk}
                onCancel={handleCancel}
                okButtonProps={{ disabled: !isDirty }}
                okText={t('submit')}
            >
                <Space direction="vertical" size="middle" className="full-width">
                    <GrispiInput
                        name="name"
                        label={t('name.label')}
                        control={control}
                        allowClear
                        autoComplete="organization"
                    />
                </Space>
            </Modal>
        </>
    );
}
